import React, { useEffect, useState } from 'react';
import './index.css';
import { ReactSortable } from 'react-sortablejs';
import { Drawer, message, Tooltip } from 'antd';
import { Card as CardType, Card } from '../../components/CardItem/CardTypes';
import CardItem from '../../components/CardItem/cards';
import * as AntIcons from '@ant-design/icons';
import { AppstoreOutlined, EditOutlined } from '@ant-design/icons';
import Search from 'antd/es/transfer/search';
import axiosApiInstance from '../../api/axiosClient';
import { useCan } from '../../context/auth_atoms';
import { UserAuth } from '../../context/AuthContext';

const DraggableCard = ({
    card,
    isDisplayed,
    onRemove,
    shouldShake,
    onColorChange,
}) => {
    const [isExploding, setIsExploding] = React.useState(false);

    const handleRemove = () => {
        setIsExploding(true);
        setTimeout(() => {
            if (onRemove) onRemove();
        }, 500); // Match the duration of the animation
    };

    return (
        <div
            key={card.id}
            className={`draggable-card ${shouldShake ? 'shake' : ''} ${
                isExploding ? 'exploding' : ''
            }`}
        >
            <CardItem
                card={card}
                isDisplayed={isDisplayed}
                onToggle={() => {
                }}
                onRemove={handleRemove}
                isShakeMode={shouldShake}
                avatarColor={
                    card.color ||
                    'linear-gradient( 135deg,  #c9c9c9 0%,  #b3b3b3 25%,  #e0e0e0 50%,  #aaaaaa 75%,  #d5d5d5 100%)'
                } // Default color if not set
                onColorChange={onColorChange}
            />
        </div>
    );
};

const Index = ({ userID }: { userID: string }) => {
  const [searchCards, setSearchCards] = useState<Card[]>([])
  const [cards, setCards] = useState<Card[]>([])
  const [allCards, setAllCards] = useState<Card[]>([])
  const [isDrawerVisible, setIsDrawerVisible] = useState(false)
  const [isTabsVisible, setIsTabsVisible] = useState(false)
  const [userCardPreferences, setUserCardPreferences] = useState([])
  const [isShakeMode, setIsShakeMode] = useState(false)
  const { user } = UserAuth()
  const can = useCan()
  const [visibleApps, setVisibleApps] = useState<Card[]>([])
  const [layoutCards, setLayoutCards] = useState<Card[]>([])
  const [showNewUserBG, setShowNewUserBG] = useState(false)

    useEffect(() => {
        const getVisibleCards = async () => {
            try {
                const res = await axiosApiInstance.get('/api/layout/apps/available', {
                    params: {
                        // user_id: userID,
                    },
                });
                const cards = res.data.map((card) => ({
                    ...card,
                    title: card.name,
                }));
                setVisibleApps(cards);
                setAllCards(cards);
            } catch (err) {
                console.error('Failed to fetch visible cards:', err);
            }
        };

        getVisibleCards();
    }, [userID]);
    // console.log('Visible apps in menu:', visibleApps);

  useEffect(() => {
    const fetchLayoutCards = async () => {
      try {
        const layout = await axiosApiInstance.get(`/api/layout/home-layout/`)
        if (layout.data.newUser) {
          // Show onboarding or alternative landing page
          setShowNewUserBG(true)
          setLayoutCards([]) // or whatever is appropriate
          console.log("newUser?", layout.data.newUser)
          return
        }
        setShowNewUserBG(false)
        
        console.log("Layout API response:", layout.data.layout)
        const layoutCards = layout.data.layout.map((layoutCard) => {
          const fullCard = allCards.find((card) => card.id === layoutCard.id)
          return {
            ...fullCard,
            ...layoutCard,
          }
        })

                setLayoutCards(layoutCards); // Store the resolved cards in state
            } catch (error) {
                // message.error("Failed to fetch layout preferences.", 3)
                // console.error("Failed to fetch preferences:", error)
            }
        };

        fetchLayoutCards();
    }, [userID, allCards]);

    const cardsToGroup = searchCards.length > 0 ? searchCards : visibleApps;
    const groupedApps = cardsToGroup.reduce(
        (groups: Record<string, CardType[]>, card: CardType) => {
            const category = card.category || 'Uncategorized';
            if (!groups[category]) groups[category] = [];
            groups[category].push(card);
            return groups;
        },
        {} as Record<string, CardType[]>,
    );
    // console.log('Grouped apps:', groupedApps);

    const savePreferences = async () => {
        // console.log('safe preferces hit');
        try {
            await axiosApiInstance.put(`/api/layout/home-layout/`, {
                user_id: userID,
                layout: userCardPreferences.map((pref) => ({
                    id: pref.id,
                    visible: pref.visible,
                    order: pref.order,
                    color: pref.color,
                })),
            });
            message.success('Layout saved successfully! ', 3);
        } catch (error) {
            message.error('Failed to save layout preferences.', 3);
            // console.error("Failed to save preferences:", error)
        }
    };

    const toggleDrawer = () => {
        setIsDrawerVisible(!isDrawerVisible);
        setIsTabsVisible(!isTabsVisible);
    };

    const addCard = (card) => {
        if (!cards.some((c) => c.id === card.id)) {
            const updatedPreferences = [
                ...userCardPreferences,
                {
                    id: card.id,
                    visible: true,
                    order: userCardPreferences.length + 1,
                    color:
                        card.color ||
                        'linear-gradient( 135deg,  #c9c9c9 0%,  #b3b3b3 25%,  #e0e0e0 50%,  #aaaaaa 75%,  #d5d5d5 100%)',
                },
            ];

            setCards([...cards, card]);
            setLayoutCards([...layoutCards, card]);
            savePreferences();
        }
    };

    const onRemove = (cardId) => {
        const updatedPreferences = userCardPreferences
            .filter((pref) => pref.id !== cardId)
            .map((pref, index) => ({
                ...pref,
                order: index + 1,
            }));

        const updatedCards = cards.filter((card) => card.id !== cardId);
        const updatedLayoutCards = layoutCards.filter((card) => card.id !== cardId);

        setUserCardPreferences(updatedPreferences);
        setCards(updatedCards);
        setLayoutCards(updatedLayoutCards);
    };

    const toggleShakeMode = () => {
        setIsShakeMode(!isShakeMode);
        setLayoutCards(
            layoutCards.map((card) => ({
                ...card,
                shouldShake: !isShakeMode,
            })),
        );
    };

    const handleColorChange = (id: string, color: string) => {
        setLayoutCards((prev) => prev.map((card) => (card.id === id ? { ...card, color } : card)));
        setUserCardPreferences((prev) => prev.map((pref) => (pref.id === id ? { ...pref, color } : pref)));
    };

  return  (
    <div style={{ display: "flex", height: "100vh", position: "relative" }}>
            {showNewUserBG && (
                <div
                    className="new-user-bg"
                    style={{
                        position: "absolute",
                        top: "40%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",   
                        width: "70vw",
                        height: "10vh",
                        zIndex: 999,
                        display: "flex",
                        fontSize: "2rem",
                        fontWeight: 500,
                    }}
                >
          Welcome, {user.displayName}!<br/>
          Start by setting up your dashboard.
        </div>
      )}
      <ReactSortable
        animation={200}
        className={`sortable-list${showNewUserBG ? "-new-user" : ""}`}
        list={layoutCards}
        group={{
          name: "shared",
          pull: true,
          put: true,
        }}
        setList={(newList) => {
          const unique = newList.filter((card, idx, self) => {
            const key = card.baseId ?? card.id
            return idx === self.findIndex((c) => (c.baseId ?? c.id) === key)
          })
          const updatedPreferences = unique.map((card, index) => ({
            id: card.id,
            visible: true,
            order: index + 1,
            color:
              card.color ||
              "linear-gradient( 135deg,  #c9c9c9 0%,  #b3b3b3 25%,  #e0e0e0 50%,  #aaaaaa 75%,  #d5d5d5 100%)", // Default color if not set
          }))
          // console.log("className", className)
          setLayoutCards(unique)
          // To see the full className, you can log it here:
          // For example, log the className of the first element in the sortable list (if it exists)
          if (document.querySelector(".sortable-list")) {
            console.log(
              "Sortable list className:",
              document.querySelector(".sortable-list")?.className
            )
          }
          setUserCardPreferences(updatedPreferences)
        }}
        disabled={!isShakeMode}
        onAdd={(evt) => {
          const newCardElement = evt.item
          if (newCardElement) {
            newCardElement.classList.remove("card-drop-animate")
            void newCardElement.offsetWidth // Force reflow
            newCardElement.classList.add("card-drop-animate")
            setTimeout(() => {
              newCardElement.classList.remove("card-drop-animate")
            }, 400)
          }
          const cardId = newCardElement?.getAttribute("data-id")

                    if (cardId && cards.some((c) => c.id === cardId)) {
                        // Remove the duplicate DOM element since Sortable will try to add it
                        if (newCardElement && newCardElement.parentNode) {
                            newCardElement.parentNode.removeChild(newCardElement);
                        }
                        return;
                    }

                    // Add new card from allCards
                    const addedCard = allCards.find((c) => c.id === cardId);
                    if (addedCard) {
                        setCards((prevCards) => [...prevCards, { ...addedCard }]);
                    }
                }}
                style={{
                    overflow: 'visible',
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr)',
                    gridTemplateRows: 'repeat(6, 1fr)',
                    gap: '4px',
                    rowGap: '4px',
                    padding: '8px',
                    width: '100%',
                    height: 'calc(100vh - 32px)',
                }}
            >
                {layoutCards.map((card) => (
                    <div key={card.id} className='draggable-card'>
                        <DraggableCard
                            shouldShake={isShakeMode}
                            card={card}
                            isDisplayed
                            onRemove={() => onRemove(card.id)}
                            onColorChange={handleColorChange}
                        />
                    </div>
                ))}
            </ReactSortable>
            {/* Toggle Button */}
            <div style={{}}>
                <Tooltip
                    title='Open the Apps menu'
                    placement='left'
                    mouseEnterDelay={1}
                >
                    <AppstoreOutlined
                        typeof='button'
                        onClick={toggleDrawer}
                        className='floating-action'
                        style={{
                            color: isDrawerVisible ? ' #0099ff' : 'grey',
                            fontSize: '30px',
                            width: '75px',
                            height: ' 75px',
                            position: 'fixed',
                            top: '55px',
                            alignItems: 'center',
                            animation: 'slide-in 0.34s ease',
                            transition: 'right 0.34s ease ,color 0.3s ease',
                            right: isTabsVisible ? 378 : 1,
                            cursor: 'pointer',
                            borderRadius: '10px 0 0 10px',
                            padding: '8px',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    />
                </Tooltip>
                <Tooltip title='Save your layout' placement='left' mouseEnterDelay={1}>
                    <AntIcons.SaveTwoTone
                        typeof='button'
                        onClick={savePreferences}
                        className='floating-action save-action'
                        style={{
                            color: isDrawerVisible ? ' #0099ff' : 'grey',
                            fontSize: '24px',
                            width: '75px',
                            height: ' 75px',
                            position: 'fixed',
                            transition:
                                'top 0.3s ease, right 0.34s ease, color 0.3s ease, opacity 0.3s ease',
                            opacity: isShakeMode ? 1 : 0,
                            top: isShakeMode ? 205 : '130px',
                            alignItems: 'center',
                            animation: 'slide-in 0.34s ease',
                            right: isTabsVisible ? 378 : 1,
                            cursor: 'pointer',
                            borderRadius: '10px 0 0 10px',
                            padding: '8px',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                    </AntIcons.SaveTwoTone>
                </Tooltip>
                <Tooltip
                    title='Toggle editing mode'
                    placement='left'
                    mouseEnterDelay={1}
                >
                    <EditOutlined
                        typeof='button'
                        onClick={toggleShakeMode}
                        className='floating-action edit-action'
                        style={{
                            color: isDrawerVisible ? ' #0099ff' : 'grey',
                            fontSize: '24px',
                            width: '75px',
                            height: ' 75px',
                            position: 'fixed',
                            animation: 'slide-in 0.34s ease',
                            transition: 'right 0.34s ease ,color 0.3s ease',
                            top: '130px',
                            alignItems: 'center',
                            right: isTabsVisible ? 378 : 1,
                            cursor: 'pointer',
                            borderRadius: '10px 0 0 10px',
                            padding: '8px',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        {isShakeMode ? 'Stop Shaking' : 'Edit Mode'}
                    </EditOutlined>
                </Tooltip>
            </div>
            {/* Collapsible Menu */}
            <Drawer
                open={isDrawerVisible}
                push
                mask={false}
                closeIcon={''}
                onClose={toggleDrawer}
                placement='right'
                title='Apps Menu    ‎ ‎ ‎ ‎ ‎ ‎ ' //it would not get centerd any other way i tried
                className='apps-drawer'
                drawerStyle={{
                    scrollBehavior: 'smooth',
                    position: 'fixed',
                    right: -15,
                    width: '393px',
                    height: '100%',
                    // background and border moved to CSS to support dark-mode
                    // boxShadow: "0 0 5px rgba(0, 0, 0, 0.2)",
                    transition: 'right 0.3s ease',
                    padding: '16px',
                    zIndex: 999,
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflowY: 'hidden',
                    overflowX: 'hidden',
                    scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
                    scrollbarWidth: 'thin',
                }}
                style={{ alignItems: 'center', justifyContent: 'center' }}
            >
                <div className='apps-drawer-inner' style={{ padding: '5px' }}>
                    <Search
                        onChange={(event) => {
                            const value = (event.target as HTMLInputElement).value;
                            const filteredCards = value
                                ? visibleApps.filter(
                                    (card) => card.title.toLowerCase().includes(value.toLowerCase()) ||
                                        card.category?.toLowerCase().includes(value.toLowerCase()),
                                )
                                : visibleApps;
                            // console.log('Search value:', value);
                            // console.log('Filtered cards:', filteredCards);
                            setSearchCards(filteredCards);
                        }}
                        placeholder='Search for apps or categories'
                    />
                </div>

                {Object.entries(groupedApps).map(([category, cards]) => (
                    <div
                        key={category}
                        style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginBottom: '16px',
                            paddingTop: '16px',
                        }}
                    >
                        <h3
                            className='category'
                            style={{
                                textAlign: 'center',
                                marginBottom: '8px',
                                paddingBottom: '6px',
                            }}
                        >
                            {category}
                        </h3>
                        <ReactSortable
                            clone={(card) => ({
                                ...card,
                                id: `${card.id}-${Date.now()}`, // Ensure unique ID for cloned cards
                                baseId: card.id,
                            })}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '8px',
                            }}
                            list={cards}
                            setList={() => {
                            }}
                            sort={false}
                            animation={200}
                            group={{
                                name: 'shared',
                                pull: 'clone', // Allow cloning instead of moving
                                put: false,
                            }}
                        >
                            {cards.map((card) => (
                                <div className='draggable-card' key={card.id}>
                                    <DraggableCard
                                        shouldShake={false}
                                        card={card}
                                        isDisplayed={false}
                                        onRemove={undefined}
                                        onColorChange={undefined}
                                    />
                                </div>
                            ))}
                        </ReactSortable>
                    </div>
                ))}
            </Drawer>
        </div>
    );
};

export default Index;
